import React, { useEffect, useState } from 'react';
import { chatFileBlob, downloadChatFile } from '../lib/chatLink';
import { VoicePlayer } from './VoiceNote';

/**
 * A file in a chat conversation, drawn as a chip: what kind of thing it is,
 * its name, its size, and a click to save it.
 *
 * Gordon, 16 October: "I also want to be able to attach files to the chat -
 * if they are related to the ticket it should go to the ticket otherwise it
 * should remain under general." This is the second half of that - the General
 * thread and the groups keep their files in the conversation itself, so the
 * conversation has to be able to show them and hand them back.
 *
 * The styles live in pages/ChatPage.css with the rest of the chat screen, as
 * `.ch-file...`, because this is only ever drawn there.
 */

/**
 * The server's limit, repeated here so a file over it is refused before it
 * travels. 10 MB of a slow upload, only to be told "too large" at the end, is
 * the kind of thing that makes people send it by WhatsApp instead.
 */
export const MAX_CHAT_FILE_BYTES = 10 * 1024 * 1024;

/**
 * What the server writes as the message when a file is sent with no words:
 * "📎 report.pdf". It does that so the people list, the group previews, the
 * live poll and the bell all have something to say. In the conversation itself
 * the chip already says it, so that body is not drawn a second time.
 */
export const CLIP = '\u{1F4CE}';
export const isAutoBody = (body, files) => Boolean(
  files && files.length > 0 && body === `${CLIP} ${files[0].file_name}`);

/**
 * A ticket comment the composer wrote after putting a file on the ticket. A
 * ticket comment carries no list of files - the file is in the ticket's own
 * Files - so this is how the conversation recognises the note and draws it as
 * a file rather than as somebody typing an emoji.
 */
export const TICKET_FILE_NOTE = `${CLIP} Attached `;
export const isTicketFileNote = (body) => typeof body === 'string'
  && body.startsWith(TICKET_FILE_NOTE);

export const fileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// The same words the ticket's Files list uses, so a PDF is called the same
// thing on both screens.
const KIND = [
  [/^image\//, 'Image'],
  [/^application\/pdf$/, 'PDF'],
  [/^text\/csv$|sheet|excel/, 'Sheet'],
  [/^text\//, 'Text'],
  [/zip|compressed|tar|rar|7z/, 'Archive'],
  [/word|document|rtf/, 'Doc'],
  [/presentation|powerpoint/, 'Slides'],
  [/^video\//, 'Video'],
  [/^audio\//, 'Audio']
];
export const fileKind = (mime) => (KIND.find(([re]) => re.test(mime || ''))?.[1]) || 'File';

/** A paperclip, drawn rather than an emoji so it takes the theme's ink. */
export const Paperclip = () => (
  <svg className="ch-clip" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
    focusable="false">
    <path
      d="M21 11.5l-8.6 8.6a5.5 5.5 0 01-7.8-7.8l8.6-8.6a3.7 3.7 0 015.2 5.2l-8.6 8.6a1.8 1.8 0 01-2.6-2.6l7.9-7.9"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" />
  </svg>
);

/**
 * A small preview for a picture, which is most of what gets sent - a
 * screenshot of the error. Like the download, it has to be FETCHED: the
 * endpoint wants a token in a header from everybody, and an <img src> cannot
 * carry one. The object URL is let go when the chip goes, or a long
 * conversation full of screenshots would hold every one of them in memory
 * for as long as the tab is open.
 *
 * A preview that fails simply is not drawn. The chip under it still works,
 * and a broken-image icon in the middle of a conversation says "fault" when
 * nothing is wrong with the file.
 */
function Thumbnail({ file, onOpen }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let url = null;
    let gone = false;
    chatFileBlob(file.id)
      .then((blob) => {
        if (gone) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      gone = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.id]);

  if (!src) return null;
  // Out of the tab order: the chip below does the same thing and says what
  // it is, so a keyboard user meets one control per file, not two.
  return (
    <button type="button" className="ch-file-thumb-btn" onClick={onOpen} tabIndex={-1}>
      <img className="ch-file-thumb" src={src} alt={file.file_name} />
    </button>
  );
}

/**
 * One file in a message. `onError` takes the words to show when the download
 * fails - a 404 when somebody has since left the group, say - because a
 * click that silently does nothing reads as a broken button.
 */
export default function ChatFile({ file, onError }) {
  const [busy, setBusy] = useState(false);
  const isImage = /^image\//.test(file.mime_type || '');
  const isAudio = /^audio\//.test(file.mime_type || '');

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadChatFile(file);
    } catch (err) {
      if (onError) onError(err.response?.data?.error || `Could not download ${file.file_name}`);
    } finally {
      setBusy(false);
    }
  };

  // A voice note (or any sound) is played where it is, as in WhatsApp,
  // rather than saved and opened; the player keeps a download button.
  if (isAudio) {
    return (
      <span className="ch-file-wrap">
        <VoicePlayer file={file} onError={onError} />
      </span>
    );
  }

  return (
    <span className="ch-file-wrap">
      {isImage && <Thumbnail file={file} onOpen={save} />}
      <button
        type="button"
        className="ch-file"
        onClick={save}
        disabled={busy}
        title={`Download ${file.file_name}`}
      >
        <span className="ch-file-kind" aria-hidden="true">{fileKind(file.mime_type)}</span>
        <span className="ch-file-text">
          <span className="ch-file-name">{file.file_name}</span>
          <span className="ch-file-size">
            {busy ? 'Downloading…' : fileSize(file.byte_size)}
          </span>
        </span>
      </button>
    </span>
  );
}
